import axios, { AxiosInstance, AxiosResponse } from 'axios'
import type {
  Cart,
  CartCustomer,
  CartLog,
  Country,
  Session,
  TicketKey,
  TransactionStatus,
} from '@booking/types/booking'

import { toApiError } from '../lib/errors/apiError'
import { CustomerFormFields } from '../stages/CustomerDetails/types'
import { LocaleKey } from '@/src/i18n/routing'

const API_URL = process.env.NEXT_PUBLIC_API_URL

if (!API_URL) {
  // Fail fast in dev/build so you don't get mysterious "undefined/carts/..." calls
  throw new Error('NEXT_PUBLIC_API_URL is not set')
}

type ApiResponse<T> = {
  data: T
}

type ApiPayload<T> = ApiResponse<T> | T

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 15000, // good practice
  // If you need cookies across domains later:
  // withCredentials: true,
})

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.log(error)
    return Promise.reject(toApiError(error))
  },
)

function unwrapData<T>(payload: ApiPayload<T>): T | null {
  if (payload == null) return null

  if (
    typeof payload === 'object' &&
    payload !== null &&
    'data' in (payload as Record<string, unknown>)
  ) {
    const nested = (payload as ApiResponse<T>).data
    if (nested !== undefined) return nested ?? null
  }

  return payload as T
}

async function getData<T>(promise: Promise<AxiosResponse<ApiPayload<T>>>): Promise<T | null> {
  const res = await promise
  return unwrapData(res.data)
}

async function postData<T>(promise: Promise<AxiosResponse<ApiPayload<T>>>): Promise<T | null> {
  const res = await promise
  return unwrapData(res.data)
}

/** -------- Types (keep these loose if your API responses vary) -------- */
export type CartId = string

export type PaymentMethod = 'PAYPAL' | 'REDUNIQ_MB_WAY' | 'REDUNIQ' | string

type ReduniqPayload = {
  customerEmail: string
}

type MbWayPayload = {
  successPath: string
  customerPhone: string
}

type PaypalPayload = {
  successPath: string
  errorPath: string
  customerEmail: string
}

type PaymentMetaMap = {
  REDUNIQ: ReduniqPayload
  REDUNIQ_MB_WAY: MbWayPayload
  PAYPAL: PaypalPayload
}

type PaymentResponse = {
  paymentId: string
  paymentReference: string
  scriptPath?: string
  redirect?: string
}

export type BookingItem = {
  quantity: number
  category: TicketKey
}

export type ExtraItem = {
  category: string
  quantity: number
}

type TransactionRes = {
  id: string
  status: string
}

/** -------- Sessions -------- */
/*export const getCartSession = (uuid: string) => getData<unknown>(api.get(`/carts/${uuid}/session`))*/

export const getSession = (id: string) => getData<Session>(api.get(`/sessions/${id}`))

export const getSessionsByDate = (
  fromDateTime: string,
  toDateTime?: string,
  options: { includeSoldOut?: boolean } = {},
) => {
  const { includeSoldOut = true } = options
  const endDateTime = toDateTime ?? fromDateTime
  return getData<{ availabilities: Session[] }>(
    api.get(`/sessions`, {
      params: { fromDateTime, toDateTime: endDateTime, includeSoldOut },
    }),
  )
}

/** -------- Carts -------- */
export const getCart = (cartId: CartId) => getData<Cart>(api.get(`/carts/${cartId}`))

export const createCart = (payload: {
  locale: string
  sessionId?: string
  bookingItems?: BookingItem[]
  extraItems?: ExtraItem[]
}) => postData<Cart>(api.post(`/carts`, payload))

/*export const createCart = (payload: { locale?: Locale } = {}) =>
  postData<Cart>(api.post(`/carts`, payload))*/

export const refreshCart = (cartId: CartId) => postData<Cart>(api.post(`/carts/${cartId}/refresh`))

/** -------- Cart logs -------- */
export const getCartLog = (cartId: CartId) => getData<CartLog>(api.get(`/cart-logs/${cartId}`))

export const createCartLog = (cartId: CartId, payload: { customerData: CartCustomer }) =>
  postData<CartLog>(api.post(`/cart-logs/${cartId}`, payload))

/** -------- Tickets -------- */
export const addToCart = (cartId: CartId, sessionId: string, bookingItems: BookingItem[]) =>
  postData<Cart>(
    api.post(`/carts/${cartId}/items`, {
      sessionId: sessionId,
      bookingItems: bookingItems,
    }),
  )

export const associateTicket = (
  cartId: CartId,
  lineItems: {
    id: string
    name: string
    email: string
    locale: LocaleKey
  }[],
) => postData<unknown>(api.post(`/carts/${cartId}/associate`, { lineItems: lineItems }))

export const markTicketConfirmed = (lineId: string, code: string | null = null) =>
  postData<unknown>(api.post(`/tickets/${lineId}/markConfirmed`, { code }))

/** -------- Add-ons -------- */
export const addAddonsToCart = (cartId: CartId, addonKeys: string[]) =>
  postData<Cart>(api.post(`/carts/${cartId}/addons`, addonKeys))

/** -------- Checkout -------- */
export const addCheckoutData = async (cartId: CartId, data: CustomerFormFields) =>
  postData<Cart>(api.post(`/carts/${cartId}/customer`, { ...data }))

/** -------- Payments / Transactions -------- */
export const payTransaction = <PaymentMethod extends keyof PaymentMetaMap>(
  cartId: CartId,
  paymentMethod: PaymentMethod,
  payload: PaymentMetaMap[PaymentMethod],
) =>
  postData<{ cart: Cart; payment: PaymentResponse }>(
    api.post(`/carts/${cartId}/pay`, {
      paymentMethod,
      meta: payload,
    }),
  )

export const createTransaction = (cartId: CartId) =>
  postData<TransactionRes>(api.post(`/carts/${cartId}/transaction`))

export const getTransactionStatus = (cartId: CartId) =>
  getData<TransactionStatus>(api.get(`/carts/${cartId}/transaction`))

export const setPaypalTransaction = (
  cartId: CartId,
  body: { payerId: string; paymentId: string; token: string },
) => postData<unknown>(api.post(`/carts/${cartId}/setPaypal`, body))

export const finalizeTransaction = (cartId: CartId) =>
  postData<unknown>(api.post(`/carts/${cartId}/finalize`))

/** -------- Countries -------- */
export const getCountries = (locale: LocaleKey) => {
  return getData<Country[]>(api.get(`/countries`, { params: { locale } }))
}

/** -------- Discounts / Coupons -------- */

export const setCouponCode = (cartId: CartId, code: string) =>
  postData<Cart>(
    api.post(`/carts/${cartId}/coupon`, {
      code: code,
    }),
  )

export const removeCouponCode = (cartId: CartId) =>
  postData<Cart>(api.delete(`/carts/${cartId}/coupon`))

/** -------- Vouchers -------- */
/*export const addVoucherToCart = (cartId: CartId, voucherId: string, quantity: number) =>
  postData<Cart>(api.post(`/carts/voucher/add/${cartId}/${voucherId}/${quantity}`))*/

export const applyVoucher = (cartId: CartId, voucherId: string) =>
  postData<Cart>(api.post(`/carts/${cartId}/vouchers/${voucherId}`))

export const unapplyVoucher = (cartId: CartId, voucherId: string) =>
  postData<Cart>(api.delete(`/carts/${cartId}/vouchers/${voucherId}`))

export const vouchersByTransaction = (transactionId: string) =>
  postData<unknown>(api.post(`/transactions/${transactionId}/vouchers`))

/** -------- Referrer -------- */
export const setReferrer = (cartId: CartId, selected: string[], other?: string) =>
  postData<Cart>(api.post(`/carts/${cartId}/referrer`, { selected, other }))

/** -------- Local cart -------- */
export const getLocalCart = (cartId: string) => {
  return getData<CartLog>(api.get(`/cart-logs/${cartId}`))
}
